import axios, { InternalAxiosRequestConfig } from 'axios'

// Determine API base URL based on environment
const getBaseURL = () => {
  // In production, use relative URL (same domain as frontend)
  if (import.meta.env.PROD) {
    return '/api'
  }
  // In development, use env var or localhost
  return import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
}

// Create axios instance with default config
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 60000, // 60 seconds for Hub API calls
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const actingMemberId = sessionStorage.getItem('agency_acting_member_id')
    if (actingMemberId && config.headers) {
      config.headers['X-Acting-Member-Id'] = actingMemberId
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const url = originalRequest?.url || ''

    // Skip token refresh for public auth endpoints - they handle their own errors
    // Do NOT skip /auth/me/ or /auth/logout/ as they are authenticated and should try refresh
    const publicAuthEndpoints = [
      '/auth/login/',
      '/auth/register/',
      '/auth/token/refresh/',
      '/auth/verify-code/',
      '/auth/verify-email/',
      '/auth/verify-email/resend/',
      '/auth/password-reset/',
      '/auth/password-reset/confirm/',
      '/agency/login/',
      '/agency/register/',
      '/agency/verify-code/',
      '/agency/verify-code/resend/',
    ]
    const isPublicAuthEndpoint = publicAuthEndpoints.some(ep => url.startsWith(ep))
    if (isPublicAuthEndpoint) {
      return Promise.reject(error)
    }

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refresh_token')
        const response = await axios.post(
          `${getBaseURL()}/auth/token/refresh/`,
          { refresh: refreshToken }
        )

        const { access, refresh } = response.data
        localStorage.setItem('access_token', access)
        localStorage.setItem('refresh_token', refresh)

        originalRequest.headers.Authorization = `Bearer ${access}`
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed, logout user
        const userRole = localStorage.getItem('user_role')
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user_role')
        window.location.href = userRole === 'agency_user' ? '/partner/agency-login' : '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api
